/* React bindings for the motion system.

   Everything here exists so that a page can express "these cards arrive in a
   cascade" in one wrapper rather than repeating variant objects and
   `whileInView` config at forty call sites. */

import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type Variants,
} from 'motion/react'
import { EASE_OUT, FLOAT_SECONDS, PARALLAX_RANGE, revealUp, stagger } from './motion'

interface RevealProps {
  children: ReactNode
  /** Seconds. Use sparingly — a delay is a promise the content is worth it. */
  delay?: number
  className?: string
  as?: 'div' | 'section' | 'li' | 'article' | 'header'
  /** Re-run every time it scrolls back into view. Off by default: content that
   *  re-animates on every scroll pass is a distraction, not a delight. */
  repeat?: boolean
  variants?: Variants
}

/**
 * Scroll-triggered entrance. Under reduced motion the element is simply
 * present — not faded, not delayed, because a delayed fade is still a delay.
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as = 'div',
  repeat = false,
  variants = revealUp,
}: RevealProps) {
  const reduce = useReducedMotion()
  const Tag = motion[as]

  if (reduce) {
    const Plain = as
    return <Plain className={className}>{children}</Plain>
  }

  return (
    <Tag
      className={className}
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: !repeat, margin: '0px 0px -12% 0px' }}
      transition={{ delay }}
    >
      {children}
    </Tag>
  )
}

interface StaggerProps {
  children: ReactNode
  className?: string
  each?: number
  delay?: number
  /* `ol` is here because a numbered sequence — the landing page's "how it
     works" — is an ordered list, and swapping it for a `ul` to fit the type
     would be letting the animation wrapper dictate the semantics. */
  as?: 'div' | 'ul' | 'ol' | 'section'
}

/**
 * Parent for a cascading group. Pair with `<Reveal>` children, or with bare
 * `motion.li variants={revealUp}` for lists.
 *
 * Stagger is decorative. It never gates interaction: the children are in the
 * DOM and clickable from frame one, they are only visually arriving.
 */
export function Stagger({ children, className, each = 0.045, delay = 0, as = 'div' }: StaggerProps) {
  const reduce = useReducedMotion()
  const Tag = motion[as]

  if (reduce) {
    const Plain = as
    return <Plain className={className}>{children}</Plain>
  }

  return (
    <Tag
      className={className}
      variants={stagger(each, delay)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
    >
      {children}
    </Tag>
  )
}

/**
 * A number that rolls to its new value instead of swapping.
 *
 * Only worth it where the change is the *point* of the update — a cart total,
 * a price that just had a promo applied. Digits are tabular so the roll does
 * not shove the layout sideways mid-animation.
 */
const identityFormat = (n: number) => String(n)

export function RollingNumber({
  value,
  format = identityFormat,
  className,
}: {
  value: number
  format?: (n: number) => string
  className?: string
}) {
  const reduce = useReducedMotion()
  const prev = useRef(value)
  const dir = value >= prev.current ? 1 : -1
  useEffect(() => {
    prev.current = value
  }, [value])

  if (reduce) return <span className={className}>{format(value)}</span>

  return (
    <span className={`roll ${className ?? ''}`}>
      <motion.span
        key={value}
        className="roll-v tnum"
        initial={{ y: dir * 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: dir * -14, opacity: 0 }}
        transition={{ duration: 0.26, ease: EASE_OUT }}
      >
        {format(value)}
      </motion.span>
    </span>
  )
}

/**
 * A layer that drifts as it passes through the viewport.
 *
 * `depth` is signed: positive moves with the scroll (a background, which
 * appears further away), negative moves against it (a foreground). Pairing a
 * positive and a negative layer is what produces the sense of space — a single
 * layer moving on its own just looks like it is lagging.
 *
 * The scroll value is run through a spring, not used raw. A raw scroll-linked
 * transform is pinned to the scroll event and stutters on a trackpad with
 * momentum; a lightly damped spring smooths it without adding perceptible
 * delay.
 */
export function Parallax({
  children,
  depth = 1,
  className,
}: {
  children: ReactNode
  depth?: number
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 })
  const y = useTransform(smooth, [0, 1], [PARALLAX_RANGE * depth, -PARALLAX_RANGE * depth])

  if (reduce) return <div className={className}>{children}</div>

  return (
    <motion.div ref={ref} className={className} style={{ y }}>
      {children}
    </motion.div>
  )
}

/**
 * Ambient float. Long period, offset per instance so a group breathes out of
 * phase rather than pulsing in unison, which reads as a loading state.
 */
export function Float({
  children,
  delay = 0,
  distance = 8,
  className,
}: {
  children: ReactNode
  delay?: number
  distance?: number
  className?: string
}) {
  const reduce = useReducedMotion()
  if (reduce) return <div className={className}>{children}</div>

  return (
    <motion.div
      className={className}
      animate={{ y: [0, -distance, 0] }}
      transition={{
        duration: FLOAT_SECONDS,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {children}
    </motion.div>
  )
}

/**
 * An endlessly scrolling row.
 *
 * The children are rendered twice and the track translates exactly -50%, so
 * the second copy is under the cursor at the instant the first has left — that
 * is what makes the loop seamless without measuring anything. The duplicate is
 * `aria-hidden`, so a screen reader hears the list once.
 *
 * Under reduced motion it becomes an ordinary horizontally scrollable row:
 * still usable, with the content reachable by drag or keyboard, which an
 * animation that simply stops would not be.
 */
export function Marquee({
  children,
  seconds = 46,
  className = '',
}: {
  children: ReactNode
  seconds?: number
  className?: string
}) {
  const reduce = useReducedMotion()

  if (reduce) {
    return (
      <div className={`marquee marquee--static ${className}`}>
        <div className="marquee-track">{children}</div>
      </div>
    )
  }

  return (
    <div className={`marquee ${className}`}>
      <motion.div
        className="marquee-track"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: seconds, repeat: Infinity, ease: 'linear' }}
      >
        {children}
        <span className="marquee-copy" aria-hidden>
          {children}
        </span>
      </motion.div>
    </div>
  )
}

/**
 * True once the component has been on screen for a frame.
 *
 * `@starting-style` is the modern answer to this and needs no JS at all, but it
 * cannot express "start the entrance only after the route chunk resolved",
 * which is the case every page here actually has.
 */
export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])
  return mounted
}
